/**
 * Wire schemas for the Operations **Rejected** surface — the last terminal book
 * in the status-coverage spine (ADR-0048). Rejected is a *Lead Status*: a Lead
 * or Transfer that no Handling Firm accepted (routing exhausted — three
 * placements deep without acceptance, or no firms remained). Terminal.
 *
 * Unlike Closed and Dropped (which are placement-Decision-driven), Rejected is
 * **lead-driven**: the population is leads at Lead Status = "Rejected", fetched
 * directly. So there is no "show-don't-drop" anomaly concept here — every row IS
 * a resolved lead.
 *
 * Scale is what makes this surface different from its siblings. Rejected is the
 * largest book in the system (1000+), so it is NOT rendered flat like the
 * dozens-of-records terminal books. v1 ships a **recent window** — the most
 * recent N by Creation Date — as cards; a search box + filters over the full
 * archive are a deferred pass. (decided 2026-05-22; see port-note rejected.md (evicted 2026-08-29, #775 round 10).)
 *
 * The card is deliberately **lead-only** — no placement fetch — so the poll
 * stays cheap at this scale:
 *   - client (the lead's own name), originating firm, case type, case state,
 *   - `firmsRouted` — how many Handling Firms it was routed to before rejection
 *     (1–3, capped by the reroute rule). This is the length of the lead's
 *     placement-link array, free off the lead record; it does NOT require
 *     reading the placements.
 *   - `createdDate` — the matter's date created (the user-set Creation Date).
 *
 * Deliberately NOT here (v1):
 *   - **Date rejected.** No Lead field records when a status flipped to
 *     Rejected; the only source is the final placement's decision date, which
 *     would mean fetching placements for every rejected lead — too heavy at this
 *     scale. Deferred (likely a real Airtable field + automation, like Dropped's
 *     OF-notified). Because there is no rejection date, "recent" means recent by
 *     *creation* date — the window's only available recency proxy.
 *   - Search + filters over the full archive (OF / case type / state).
 *
 * All derivation is read-only and live — no stored Rejected state.
 *
 * Spec: engineering/command-center/docs/port-notes/rejected.md (evicted 2026-08-29, #775 round 10)
 */

import { z } from "zod";

/**
 * One rejected matter. The unit is the **lead** itself (Lead Status =
 * Rejected); its originating firm name is joined by the composer.
 */
export const RejectedCaseSchema = z.object({
  /** The lead's Airtable record ID — the row's identity. */
  id: z.string(),
  /** Stable human-readable Lead ID. */
  leadId: z.string(),
  /** Client name, or null when the lead carries no name (defensive). */
  client: z.string().nullable(),

  /** Joined Originating Firm name, or null when unmatched / link broken.
   *  When the OF is LegalFlare this reads "LegalFlare". */
  originatingFirmName: z.string().nullable(),
  /** Case-type label as recorded by the parser, or null. */
  caseType: z.string().nullable(),
  /** Two-letter US state of the incident (the matter's jurisdiction), or null. */
  caseState: z.string().nullable(),

  /** How many Handling Firms the matter was routed to before rejection — the
   *  count of the lead's placement links (1–3, capped by the reroute rule).
   *  Derived without reading the placements. */
  firmsRouted: z.number().int().nonnegative(),

  /** The matter's date created (user-set Creation Date, ISO YYYY-MM-DD), or
   *  null. Doubles as the window's recency key — see the date-rejected note. */
  createdDate: z.string().nullable(),
});
export type RejectedCase = z.infer<typeof RejectedCaseSchema>;

/**
 * Payload for `GET /api/rejected`. `cases` is the recent window, most-recent
 * (by Creation Date) first. `windowSize` is the cap requested — when
 * `cases.length === windowSize` the book is larger than the window and the
 * surface says so ("N most recent"); the full archive is the deferred
 * search/filter pass.
 */
export const RejectedResponseSchema = z.object({
  cases: z.array(RejectedCaseSchema),
  windowSize: z.number().int().positive(),
});
export type RejectedResponse = z.infer<typeof RejectedResponseSchema>;
